import { inject, observer } from 'mobx-react';
import React from 'react';
import { EqualHeight, Grid, GridColumn, Icon, Separator, Text } from 'telia-front-react';
import { TranslationsContext } from '@teliaee/telia-translate';
import { capitalize } from '../../util/TextUtil';
import { Package } from '../MobileBusinessInterfaces';
import { twebTranslateOrEmpty } from '../../util/TranslateUtil';
import LanguageStore from '../../stores/LanguageStore';

interface Props {
  mobilePackage: Package;
  languageStore?: LanguageStore;
}

@observer
class PackageCardContent extends React.Component<Props> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  renderMsFeatures(): JSX.Element {
    const translate = this.context.translate;

    return (
      <>
        <Grid noWrap={true} align={['center-xs', 'middle-xs']} gutter="small" className="text-left">
          <GridColumn width={['min']}>
            <Icon modifiers="#office" size="sm" align="none" />
          </GridColumn>
          <GridColumn width={['max']} className="text-small">
            <p>{translate('mobileBusiness.microsoft365')}</p>
          </GridColumn>
        </Grid>
        <Separator spacing="xsmall" weight="thin" />
      </>
    );
  }

  private getEuIcon(): string {
    const {
      languageStore: { currentLanguage }
    } = this.props;

    switch (currentLanguage) {
      case 'et':
        return 'eu-sliced-et';
      case 'ru':
        return 'eu-sliced-ru';
      case 'en':
      default:
        return 'eu-sliced';
    }
  }

  renderFeatureBlock(icon: string, text: string): JSX.Element {
    return (
      <Grid noWrap={true} align={['center-xs', 'middle-xs']} horizontalGutter="small" verticalGutter="none" className="text-left">
        <GridColumn width={['min']}>
          <Icon modifiers={`#${icon}`} size="sm" align="none" />
        </GridColumn>
        <GridColumn width={['max']} className="text-small">
          {text}
        </GridColumn>
      </Grid>
    );
  }

  renderFeatures(): JSX.Element {
    const { translate, translateOrEmpty } = this.context;
    const { mobilePackage } = this.props;
    const { isUnlimitedPlus, is5G } = mobilePackage;

    const contactIcon = isUnlimitedPlus ? 'newcontact' : 'contact';
    const contactText = twebTranslateOrEmpty(translateOrEmpty, `mobileBusiness.secondListItem${mobilePackage.capacity}`)
      ? translate(`mobileBusiness.secondListItem${mobilePackage.capacity}`)
      : translate('mobileBusiness.secondListItem');
    const euIcon = this.getEuIcon();
    const euText = translate(isUnlimitedPlus ? 'mobileBusiness.euCapacityPremium' : 'mobileBusiness.euCapacity', { capacity: mobilePackage.euCapacity });

    return (
      <>
        <EqualHeight group="users">{this.renderFeatureBlock(contactIcon, contactText)}</EqualHeight>
        <EqualHeight group="roaming">{this.renderFeatureBlock(euIcon, euText)}</EqualHeight>
        <EqualHeight group="5g">{is5G && this.renderFeatureBlock('service-internet', translate('mobileBusiness.5gInternet'))}</EqualHeight>
      </>
    );
  }

  public render() {
    const translate = this.context.translate;
    const { mobilePackage } = this.props;

    return (
      <Text>
        {mobilePackage.hasMs365 && this.renderMsFeatures()}
        {this.renderFeatures()}
        <Separator spacing="xxsmall" color="invisible" />
        <div>
          <p className="text-light text-small">{translate('mobileBusiness.fromEstoniaToLocalNetworks')}</p>
          <p className="text-light text-small text-bold">
            {translate('mobileBusiness.localCallsAndMessages', {
              calls: mobilePackage.calls
                ? `${mobilePackage.calls} ${translate('mobileBusiness.callsUnit')}`
                : capitalize(translate('mobileBusiness.unlimited')),
              messages: mobilePackage.messages
            })}
          </p>
        </div>
        <div>
          <p className="text-light text-small">{translate('mobileBusiness.fromEstoniaToBalticsAndScandinavia')}</p>
          <p className="text-light text-small text-bold">
            {translate('mobileBusiness.callsAndMessages', {
              calls: mobilePackage.balticScandinavianCalls,
              messages: mobilePackage.balticScandinavianMessages
            })}
          </p>
        </div>
      </Text>
    );
  }
}

export default inject('languageStore')(PackageCardContent);
